import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const MozambiquePage = () => {
  const mozambiqueData = {
    name: 'Mozambique',
    region: 'Southern Africa',
    color: '#708080',
    metaTitle: 'Pharma & Nutraceutical Registration in Mozambique | ANARME | ZAZIBONA | TheApostrophe',
    metaDescription: 'Register pharmaceutical and nutraceutical products in Mozambique via ANARME. ZAZIBONA-eligible CTD dossier preparation, Portuguese labelling, Simplified and Complete registration pathways, and SADC market expansion support.',

    hero: {
      title: 'Pharma & Nutraceutical Registration in Mozambique',
      subtitle: 'A SADC-member, ZAZIBONA-participating southern African market with near-total import dependence, a dual infectious-NCD disease burden, and a strategic gateway to nine SADC countries through a single dossier pathway.',
      cta: 'Start Your Mozambique Registration',
      secondaryCta: null
    },

    atAGlance: [
      { label: 'Population', value: '~33 million' },
      { label: 'Income Level', value: 'Low-income' },
      { label: 'Health Expenditure per Capita', value: '~USD 40–50' },
      { label: 'Health Expenditure (% of GDP)', value: '~7.8%' },
      { label: 'Urbanisation', value: '~38–39% urban' },
      { label: 'Life Expectancy', value: '~57–59 years' }
    ],

    marketOverview: {
      title: 'Market Overview: Import-Driven with Regional Strategic Value',
      stats: [
        'Mozambique imports the overwhelming majority of its finished pharmaceuticals — local manufacturing remains nascent',
        'Key import suppliers include India, South Africa, Portugal, Brazil, and China',
        'Maputo (capital and port city) serves as the primary entry point for medicines; Beira and Nacala ports provide further distribution reach',
        'ANARME (Autoridade Nacional Reguladora de Medicamentos, IP) is the national regulatory authority, established under Law 4/98 and operational as a semi-autonomous body since 2019',
        'Mozambique is an active member of ZAZIBONA, the SADC collaborative medicines registration initiative — a single dossier submission can unlock registration decisions across up to nine participating SADC countries',
        'Strong demand for essential generics across anti-infectives, antimalarials, antiretrovirals, and maternal-child health products',
        'NCD burden (hypertension, diabetes) growing steadily in urban centres, particularly Maputo and Beira',
        'Nutraceutical and supplement market is at an early but growing stage — immunity, blood builders, vitamins, and maternal supplements showing uptake'
      ],
      opportunities: [
        'Near-total import dependence creates strong demand for foreign-manufactured generics',
        'ZAZIBONA pathway allows one submission to cover multiple SADC markets simultaneously',
        'High HIV, malaria, and TB burden creates sustained, predictable demand for anti-infectives',
        'Mozambique\'s pharmaceutical imports exceeded USD 300M in 2021 and continue to grow',
        'Indian-manufactured generics are competitively priced and well-suited to the market\'s affordability expectations',
        'Malnutrition and maternal health needs drive consistent demand for nutraceutical and supplement products'
      ]
    },

    diseaseburden: {
      title: 'Disease Landscape: Mozambique\'s Healthcare Needs',
      subtitle: 'Mozambique carries one of the heaviest combined infectious and nutritional disease burdens in sub-Saharan Africa, with NCDs rising rapidly in urban areas.',
      categories: [
        {
          name: 'Infectious Diseases – Major Demand Drivers',
          items: [
            'Malaria: Endemic nationwide; entire population at risk; one of the leading causes of under-five child deaths; annual burden exceeds 10 million cases',
            'HIV/AIDS: Among top eight countries globally for HIV prevalence; approximately 2.5 million people living with HIV; MTCT rate is the second highest in the world',
            'Tuberculosis: Rising incidence, particularly paediatric TB; strong TB-HIV co-infection burden; WHO high-burden country',
            'Diarrhoeal diseases and respiratory infections: Significant contributors to child morbidity and mortality',
            'Portfolio: Antimalarials (ACT, chloroquine), ARVs, anti-TB fixed-dose combinations, paediatric antibiotics, ORS + zinc, antifungals, antiparasitics'
          ]
        },
        {
          name: 'Growing Non-Communicable Diseases',
          items: [
            'Hypertension: Rapidly increasing in urban populations — Maputo, Beira, Nampula',
            'Diabetes: Driven by urbanisation, dietary shifts, and low diagnosis rates',
            'Cardiovascular and metabolic risk growing among working-age adults',
            'Portfolio: Amlodipine, enalapril, metformin, glibenclamide, atenolol, simvastatin',
            'Affordable generics with proven efficacy are the preferred treatment option across all income levels'
          ]
        },
        {
          name: 'Maternal, Paediatric & Nutritional Health',
          items: [
            'High rates of maternal anaemia and malnutrition',
            'Stunting affects approximately 38% of children under five',
            'Acute malnutrition remains a persistent national challenge',
            'Paediatric multivitamins, iron-folic acid combinations, calcium + D3, ORS, and dewormers in consistent demand',
            'Nutraceuticals: Maternal and paediatric health supplements, blood builders, and immunity products hold strong commercial potential in urban pharmacies'
          ]
        }
      ]
    },

    regulatoryFramework: {
      authority: 'ANARME — Autoridade Nacional Reguladora de Medicamentos, IP',
      description: 'Mozambique\'s semi-autonomous national medicines regulatory authority, established under the Medicines Law (Law 4/98) with its current institutional structure under Decree 22/99 and subsequent reforms. ANARME, IP has been actively supported by USAID/USP\'s PQM+ programme to strengthen its registration review, quality control, and post-market surveillance functions. ANARME manages the Pharmadex digital platform to track and manage registration submissions. Applications must be submitted by a company with a registered office in Mozambique.',
      pharma: {
        timeline: '6–12 months for Simplified Registration; up to 18–24 months for Complete Registration depending on dossier complexity and ANARME\'s review queue',
        requirements: [
          'Application can only be submitted by a registered entity with a legal address in Mozambique (local authorized holder/representative mandatory)',
          'CTD-aligned dossier structure; ANARME accepts submissions referencing ICH-CTD format',
          'Module 1 (Administrative) documentation in Portuguese',
          'Stability data required for tropical conditions (ICH Zone IVb: 40°C/75% RH)',
          'Indian WHO-GMP certification widely accepted',
          'Physical (hand-delivered) submission to ANARME upon appointment',
          'ANARME may request product samples for quality laboratory testing',
          'Summary of Product Characteristics (SmPC) and package insert required in Portuguese',
          'Labels must carry product name, active substance composition, pharmaceutical form, expiry, registration number, storage conditions, and MAH name — all in Portuguese'
        ],
        insight: 'Mozambique follows a CTD-compatible structure. ANARME issues deficiency letters based on gaps in chemistry-pharmaceutical documentation, stability data, and label compliance. Portuguese-language labelling and inserts are mandatory. A registered local entity to hold the marketing authorization is non-negotiable.'
      },
      nutra: {
        timeline: 'Varies by classification, claims profile, and documentation quality',
        requirements: [
          'Classification by ANARME depends on the product\'s claims, composition, and intended use presentation',
          'Full quantitative formula required',
          'Manufacturer GMP/QMS certificate',
          'Certificate of Analysis (COA) with microbial limits',
          'Safety data for non-traditional or novel ingredients',
          'CFS (Certificate of Free Sale) from country of origin',
          'Artwork and labelling in Portuguese (mandatory)',
          'Claims must not make therapeutic or disease-treatment assertions — functional and nutritional claims language only',
          'No standalone nutraceutical registration guideline exists; products are assessed on a case-by-case basis under the broader medicines/food law framework'
        ],
        insight: 'Mozambique\'s nutraceutical regulatory pathway is evolving. Urban consumers in Maputo and Beira are increasingly adopting vitamins, immune boosters, blood tonics, and maternal supplements. Early-mover advantage is real for compliant importers.'
      }
    },

    pharmaPathway: {
      title: 'Pharmaceutical Registration (ANARME — Definite Registration)',
      steps: [
        'Local Entity Appointment — Appoint a registered company with a Mozambique office to act as marketing authorization holder',
        'Pathway Selection — Determine eligibility for Simplified (reference country) or Complete (full dossier) registration',
        'Dossier Compilation — CTD-aligned structure; Portuguese administrative module; stability data (Zone IVb); GMP certificates; SmPC and label in Portuguese',
        'Pharmadex Pre-Registration — Register submission intent on ANARME\'s digital tracking system',
        'Physical Submission — Hand-delivered to ANARME in Maputo upon appointment',
        'ANARME Screening — Completeness check; deficiency letter issued if gaps identified',
        'Technical Evaluation — Chemistry-pharmaceutical, safety, and efficacy review',
        'Sample Testing — ANARME\'s quality control laboratory may request product samples',
        'Query / Deficiency Response — Respond to ANARME\'s questions promptly; resubmission within ANARME\'s specified timeline',
        'Marketing Authorization Issuance — Valid 5 years; subject to renewal'
      ]
    },

    nutraPathway: {
      title: 'Nutraceutical / Dietary Supplement Registration',
      steps: [
        'Product classification assessment based on claims, composition, and intended use',
        'Full quantitative formula preparation',
        'Manufacturer GMP/QMS certification',
        'COA + microbial limits documentation',
        'CFS / Certificate of Free Sale from country of origin',
        'Safety data for non-traditional ingredients',
        'Portuguese artwork and label preparation',
        'Claims language review — functional/nutritional only',
        'Submission to ANARME for evaluation',
        'Marketing authorization issuance'
      ]
    },

    requiredDocs: {
      'Pharmaceuticals': [
        'Application form (Portuguese — administrative section)',
        'CTD-compliant dossier: Parts 1 (Administrative), 2 (Chemical-Pharmaceutical), 3 (Safety), 4 (Efficacy)',
        'WHO-GMP certificate of the manufacturing site',
        'CoPP (Certificate of Pharmaceutical Product) — strongly recommended',
        'Stability data (ICH Zone IVb: 40°C/75% RH)',
        'API/DMF documentation',
        'Process validation data',
        'SmPC, package insert, and label artwork (all in Portuguese)',
        'COA and batch quality documents',
        'Product samples (if requested by ANARME)',
        'Power of attorney / authorisation for local representative'
      ],
      'Nutraceuticals': [
        'Full quantitative composition and COA',
        'GMP/QMS certificate',
        'Safety justification for non-traditional ingredients',
        'Artwork and label in Portuguese',
        'Product specifications',
        'CFS / Certificate of Free Sale from India'
      ]
    },

    whyThisCountry: {
      title: 'Why Mozambique Should Be Part of Your Southern Africa Strategy',
      reasons: [
        'Near-total pharmaceutical import dependence — virtually no local competition for generics',
        'Sustained, high-volume demand for anti-infectives, antimalarials, and ARV-adjacent products',
        'Growing NCD burden creates long-term market for chronic therapy generics',
        'Indian manufacturers are competitively positioned on pricing and GMP acceptance',
        'Mozambique\'s pharmaceutical imports are well above USD 300M annually and growing',
        'Maputo, Beira, and Nacala ports offer good logistics connectivity into the SADC region',
        'Strong maternal and child health need supports nutraceutical and supplement portfolio expansion'
      ],
      bestPortfolio: [
        'Anti-infectives (amoxicillin, cotrimoxazole, azithromycin, metronidazole), antimalarials (ACT), paediatric formulations',
        'ARV-support and co-infection products (where applicable)',
        'Chronic disease generics: antihypertensives, antidiabetics, statins',
        'Maternal and paediatric nutraceuticals: iron-folic acid, multivitamins, calcium + D3, ORS, dewormers',
        'Immunity supplements, blood tonics, and herbal wellness products (urban pharmacies)'
      ]
    },

    howWeHelp: {
      title: 'How TheApostrophe Helps You Register in Mozambique',
      services: [
        'Product-market feasibility analysis specific to Mozambique\'s disease burden and import landscape',
        'ANARME-compliant CTD dossier preparation (Simplified and Complete pathways)',
        'Portuguese artwork and label compliance',
        'Nutraceutical documentation support and claims language review',
        'Local authorized representative / marketing authorization holder identification',
        'Sample testing coordination with ANARME\'s quality laboratory',
        'Query handling, deficiency response, and lifecycle support (renewals, variations)'
      ]
    },

    faqs: [
      {
        question: 'Does Mozambique require a local representative for registration?',
        answer: 'Yes. ANARME requires that all applications be submitted by a company with a registered office in Mozambique. A local authorized holder/representative is mandatory for all registrations.'
      },
      {
        question: 'Is Portuguese-language labelling mandatory?',
        answer: 'Yes. All pharmaceutical labels, package inserts, and SmPC documents must be in Portuguese. ANARME will not accept English-only artwork.'
      },
      {
        question: 'Is Indian GMP accepted by ANARME?',
        answer: 'Yes. WHO-GMP certification issued by Indian regulatory authorities (CDSCO-approved sites) is accepted by ANARME. Documentary review is the standard approach; physical GMP site inspections by ANARME are not typical for Indian facilities.'
      },
      {
        question: 'What is the registration validity period?',
        answer: 'Definite registration is valid for 5 years and may be renewed. Extraordinary registration (for products already marketed) is valid for 3 years and is not renewable.'
      },
      {
        question: 'Do nutraceuticals require ANARME registration?',
        answer: 'Nutraceuticals and food supplements are subject to ANARME assessment depending on their composition, claims, and intended use. Products with any therapeutic or health claims will be regulated. Engage with a regulatory consultant before assuming a product is exempt.'
      }
    ]
  };

  return <CountryTemplate countryData={mozambiqueData} />;
};
